import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Button,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import SearchIcon from "@mui/icons-material/Search";
import { useLanguage } from "../../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { getTopNavbar } from "../../api/topNavbar";

const FONT_SIZES = {
  small: "14px",
  medium: "16px",
  large: "18px",
};

const TopNavbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const handleSkipToContent = () => {
    window.dispatchEvent(new Event("skip-main-content"));
  };

  const [fontSize, setFontSize] = useState("medium");
  const { language, toggleLanguage } = useLanguage();
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [navbarData, setNavbarData] = useState(null);

  useEffect(() => {

    fetchNavbarData();

  }, [language]);

  const fetchNavbarData = async () => {

    try {
      const data = await getTopNavbar(language);
      console.log("navbar__", data);
      setNavbarData(data);
    } catch (err) {
      console.error(err);

    }

  };


  useEffect(() => {
    document.documentElement.style.fontSize = FONT_SIZES[fontSize];
  }, [fontSize]);

  const toggleSearch = () => {
    setShowSearch((prev) => !prev);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      const found = window.find(
        searchQuery,
        false,
        false,
        true,
        false,
        false,
        false
      );
      if (!found) {
        alert("Text not found on this page");
      }
      setSearchQuery("");
      setShowSearch(false);
    }
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: "#1f2937",
        height: 48,
        zIndex: 1400,
        top: 0,
      }}
    >
      <Toolbar
        sx={{
          minHeight: "48px !important",
          px: { xs: 1.5, sm: 2, md: 2.7 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        {/* LEFT SECTION - TOLL FREE */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: "fit-content",
          }}
        >
          {(
            <>
              <Typography
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  color: "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                {language === "mr"
                  ? "टोल-फ्री क्रमांक:"
                  : "Toll-Free No:"}
              </Typography>

              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, #ff0000d3, #ff0000d3)",
                  color: "#fff",
                  px: 1.5,
                  py: 0.3,
                  borderRadius: "8px",
                  fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.7rem" },
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  boxShadow: "0 0 8px rgba(255, 0, 0, 0.7)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  whiteSpace: "nowrap",
                }}
              >
                <a
                  href={navbarData?.tollFreeLink}
                  style={{
                    color: "#fff",
                    textDecoration: "none",
                  }}
                >
                  {navbarData?.tollFreeNumber}
                </a>
              </Box>
            </>
          )}
        </Box>

        {/* RIGHT SECTION - ALL CONTROLS */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1.2 },
            flexWrap: "nowrap",
          }}
        >
          {/* Skip + Sitemap (Moved to Right) */}
          {!isMobile && (
            <>
              <Typography
                onClick={handleSkipToContent}
                sx={{
                  fontSize: "0.75rem",
                  color: "#fff",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {language === "mr"
                  ? "मुख्य मजकुराकडे जा"
                  : "Skip to main content"}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "#9ca3af",
                }}
              >
                |
              </Typography>

              <Typography
                onClick={() => { navigate('/sitemap') }}
                sx={{
                  fontSize: "0.75rem",
                  color: "#fff",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {language === "mr" ? "साइट नकाशा" : "Site Map"}
              </Typography>
            </>
          )}

          {/* SEARCH */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                size="small"
                onClick={toggleSearch}
                sx={{ color: "#fff", p: 0.5 }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>

              {showSearch && (
                <TextField
                  size="small"
                  placeholder={
                    language === "mr" ? "शोधा..." : "Search..."
                  }
                  value={searchQuery}
                  onChange={(e) =>
                    setSearchQuery(e.target.value)
                  }
                  onKeyDown={handleSearchSubmit}
                  sx={{
                    width: { sm: 120, md: 160 },
                    ml: 0.5,
                    "& .MuiOutlinedInput-root": {
                      height: 30,
                      "& input": {
                        padding: "4px 8px",
                        fontSize: "0.75rem",
                        color: "#fff",
                      },
                      "& fieldset": {
                        borderColor: "#9ca3af",
                      },
                      "&:hover fieldset": {
                        borderColor: "#fff",
                      },
                    },
                  }}
                />
              )}
            </Box>
          )}

          {/* FONT SIZE */}
          <Stack direction="row" spacing={0.5}>
            {["small", "medium", "large"].map((size) => (
              <Button
                key={size}
                size="small"
                onClick={() => setFontSize(size)}
                sx={{
                  minWidth: { xs: 26, sm: 30 },
                  height: 24,
                  fontSize: "0.7rem",
                  color: "#fff",
                  border: "1px solid #9ca3af",
                  backgroundColor:
                    fontSize === size
                      ? "#374151"
                      : "transparent",
                  "&:hover": {
                    backgroundColor: "#374151",
                  },
                  p: 0,
                }}
              >
                {size === "small"
                  ? "A-"
                  : size === "large"
                    ? "A+"
                    : "A"}
              </Button>
            ))}
          </Stack>

          {/* LANGUAGE SWITCH */}
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
          >
            <IconButton
              size="small"
              onClick={toggleLanguage}
              sx={{ color: "#fff", p: 0.5 }}
            >
              <LanguageIcon fontSize="small" />
            </IconButton>

            {!isMobile && (
              <Typography
                onClick={toggleLanguage}
                sx={{
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  color: "#fff",
                  whiteSpace: "nowrap",
                }}
              >
                {language === "en"
                  ? "मराठी"
                  : "English"}
              </Typography>
            )}
          </Stack>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopNavbar;


// import React, { useState, useEffect } from "react";
// import {
//   AppBar,
//   Toolbar,
//   Typography,
//   Box,
//   IconButton,
//   Button,
//   Stack,
//   TextField,
//   useMediaQuery,
//   useTheme,
// } from "@mui/material";
// import LanguageIcon from "@mui/icons-material/Language";
// import SearchIcon from "@mui/icons-material/Search";
// import { useLanguage } from "../../context/LanguageContext";

// const FONT_SIZES = {
//   small: "14px",
//   medium: "16px",
//   large: "18px",
// };

// const TopNavbar = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

//   const handleSkipToContent = () => {
//     window.dispatchEvent(new Event("skip-main-content"));
//   };

//   const [fontSize, setFontSize] = useState("medium");
//   const { language, toggleLanguage } = useLanguage();
//   const [showSearch, setShowSearch] = useState(false);
//   const [searchQuery, setSearchQuery] = useState("");

//   useEffect(() => {
//     document.documentElement.style.fontSize = FONT_SIZES[fontSize];
//   }, [fontSize]);

//   const toggleSearch = () => {
//     setShowSearch((prev) => !prev);
//   };

//   const handleSearchSubmit = (e) => {
//     if (e.key === "Enter" && searchQuery.trim()) {
//       const found = window.find(searchQuery, false, false, true, false, false, false);
//       if (!found) {
//         alert("Text not found on this page");
//       }
//       setSearchQuery("");
//       setShowSearch(false);
//     }
//   };

//   return (
//     <AppBar
//       position="fixed"
//       elevation={0}
//       sx={{
//         backgroundColor: "#1f2937",
//         height: { xs: 48, sm: 48 },
//         zIndex: 1400,
//         top: 0,
//       }}
//     >
//       <Toolbar
//         sx={{
//           minHeight: "48px !important",
//           px: { xs: 1, sm: 2, md: 4 },
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "space-between",
//         }}
//       >
//         {/* LEFT SECTION */}
//         <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}>
//           {!isMobile && (
//             <>
//               <Typography
//                 onClick={handleSkipToContent}
//                 sx={{
//                   fontSize: { xs: "0.7rem", sm: "0.75rem" },
//                   color: "#fff",
//                   cursor: "pointer",
//                   "&:hover": { textDecoration: "underline" },
//                   whiteSpace: "nowrap",
//                 }}
//               >
//                 {language === "mr" ? "मुख्य मजकुराकडे जा" : "Skip to main content"}
//               </Typography>

//               <Typography sx={{ fontSize: "0.75rem", color: "#fff" }}>|</Typography>

//               <Typography
//                 sx={{
//                   fontSize: { xs: "0.7rem", sm: "0.75rem" },
//                   color: "#fff",
//                   cursor: "pointer",
//                   "&:hover": { textDecoration: "underline" },
//                   whiteSpace: "nowrap",
//                 }}
//               >
//                 {language === "mr" ? "साइट नकाशा" : "Site Map"}
//               </Typography>
//             </>
//           )}
//         </Box>

//         {/* CENTER SECTION - TOLL FREE */}
//         {!isMobile && (
//           <Box
//             sx={{
//               display: { xs: "none", sm: "flex" },
//               alignItems: "center",
//               position: "absolute",
//               left: "50%",
//               transform: "translateX(-50%)",
//               whiteSpace: "nowrap",
//               gap: 1,
//             }}
//           >
//             <Typography
//               sx={{
//                 fontSize: { xs: "0.7rem", sm: "0.75rem" },
//                 color: "#fff",
//               }}
//             >
//               {language === "mr" ? "टोल-फ्री क्रमांक:" : "Toll-Free No:"}
//             </Typography>

//             {/* Highlighted Number Badge */}
//             <Box
//               sx={{
//                 background: "linear-gradient(135deg, #ff0000, #b30000)",
//                 color: "#fff",
//                 px: 1.5,
//                 py: 0.3,
//                 borderRadius: "8px",
//                 fontSize: { xs: "0.7rem", sm: "0.75rem" },
//                 fontWeight: 700,
//                 letterSpacing: "0.5px",
//                 boxShadow: "0 0 10px rgba(255, 0, 0, 0.7)",
//                 border: "1px solid rgba(255,255,255,0.2)",
//               }}
//             >
//               1800 233 4010
//             </Box>
//           </Box>
//         )}


//         {/* RIGHT SECTION */}
//         <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 } }}>
//           {/* SEARCH - Tablet/Desktop */}
//           {!isMobile && (
//             <Box sx={{ display: "flex", alignItems: "center" }}>
//               <IconButton
//                 size="small"
//                 onClick={toggleSearch}
//                 sx={{ color: "#fff", p: 0.5 }}
//               >
//                 <SearchIcon fontSize="small" />
//               </IconButton>

//               {showSearch && (
//                 <TextField
//                   size="small"
//                   placeholder={language === "mr" ? "शोधा..." : "Search..."}
//                   value={searchQuery}
//                   onChange={(e) => setSearchQuery(e.target.value)}
//                   onKeyPress={handleSearchSubmit}
//                   sx={{
//                     width: { sm: 120, md: 150 },
//                     "& .MuiOutlinedInput-root": {
//                       height: 28,
//                       "& input": {
//                         padding: "4px 8px",
//                         fontSize: "0.75rem",
//                         color: "#fff",
//                       },
//                       "& fieldset": { borderColor: "#9ca3af" },
//                       "&:hover fieldset": { borderColor: "#fff" },
//                     },
//                   }}
//                 />
//               )}
//             </Box>
//           )}

//           {/* FONT SIZE */}
//           <Stack direction="row" spacing={0.5}>
//             {["small", "medium", "large"].map((size) => (
//               <Button
//                 key={size}
//                 size="small"
//                 onClick={() => setFontSize(size)}
//                 sx={{
//                   minWidth: { xs: 28, sm: 30 },
//                   height: { xs: 22, sm: 24 },
//                   fontSize: { xs: "0.65rem", sm: "0.7rem" },
//                   color: "#fff",
//                   border: "1px solid #9ca3af",
//                   backgroundColor: fontSize === size ? "#374151" : "transparent",
//                   "&:hover": { backgroundColor: "#374151" },
//                   p: 0,
//                 }}
//               >
//                 {size === "small" ? "A-" : size === "large" ? "A+" : "A"}
//               </Button>
//             ))}
//           </Stack>

//           {/* LANGUAGE SWITCH */}
//           <Stack direction="row" spacing={0.5} alignItems="center">
//             <IconButton
//               size="small"
//               onClick={toggleLanguage}
//               sx={{ color: "#fff", p: 0.5 }}
//             >
//               <LanguageIcon fontSize="small" />
//             </IconButton>

//             <Typography
//               onClick={toggleLanguage}
//               sx={{
//                 cursor: "pointer",
//                 fontSize: { xs: "0.7rem", sm: "0.75rem" },
//                 color: "#fff",
//                 display: { xs: "none", sm: "block" },
//               }}
//             >
//               {language === "en" ? "मराठी" : "English"}
//             </Typography>
//           </Stack>
//         </Box>
//       </Toolbar>
//     </AppBar>
//   );
// };

// export default TopNavbar;